<template>
  <div class="border-rounded mt-3">
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2" style="gap: 10px">
      <div class="text-muted">
        {{ $t('efficiencyTestDetails.comparisonCriteria.coverage.subtitle') }}
      </div>
      <div class="d-flex" style="gap: 8px">
        <b-input
          size="sm"
          v-model="isinInput"
          :placeholder="$t('efficiencyTestDetails.comparisonCriteria.coverage.isinPlaceholder')"
          style="width: 200px"
        ></b-input>
        <b-button size="sm" variant="outline-secondary" :disabled="!isinInput" @click="onIncludeByIsin">
          {{ $t('efficiencyTestDetails.comparisonCriteria.coverage.includeByIsin') }}
        </b-button>
        <b-dropdown size="sm" variant="outline-secondary" class="layout-dropdown" right>
          <template #button-content>
            <font-awesome-icon :icon="['fas', 'columns']" class="mr-1" />
            {{ $t(`efficiencyTestDetails.comparisonCriteria.coverage.layouts.${selectedLayout}`) }}
          </template>
          <b-dropdown-item v-for="layout in layouts" :key="layout" @click="selectedLayout = layout">
            {{ $t(`efficiencyTestDetails.comparisonCriteria.coverage.layouts.${layout}`) }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <div class="small">
      <b>{{ coverageStats.comparablesUsedCount || 0 }}</b>
      {{ $t('efficiencyTestDetails.comparisonCriteria.coverage.includedInAnalysis') }}
      <span class="text-muted">
        — {{ coverageStats.primaryMarketComparablesCount || 0 }}
        {{ $t('efficiencyTestDetails.comparisonCriteria.coverage.primary') }} ·
        {{ coverageStats.secondaryMarketComparablesCount || 0 }}
        {{ $t('efficiencyTestDetails.comparisonCriteria.coverage.secondary') }}
      </span>
    </div>

    <div v-for="warning in coverageWarnings" :key="warning.code + (warning.isin || '')" class="mb-2">
      <b-alert show :variant="warning.isBlocking ? 'danger' : 'warning'" class="mb-0 py-2 small">
        {{ warningText(warning) }}
        <span v-if="warning.isin"> — {{ warning.isin }}</span>
        <span v-if="warning.productName"> {{ warning.productName }}</span>
      </b-alert>
    </div>

    <b-alert
      v-if="failureBanner.visible"
      show
      :variant="failureBanner.blocking > 0 ? 'danger' : 'warning'"
      class="mb-2 py-2 small"
      data-test="failureBanner"
    >
      <div>{{ failureBannerSummary }}</div>
      <div v-for="note in failureBanner.notes" :key="note.textKey">{{ $t(note.textKey, note.textParams) }}</div>
      <div v-if="failureBanner.notApplicable > 0">
        {{ $t('efficiencyTestFailures.banner.notApplicable', { n: failureBanner.notApplicable }) }}
      </div>
    </b-alert>

    <template v-if="coverageItems.length > 0">
      <b-table
        class="pointer small mb-4"
        :items="coverageItems"
        :fields="itemsFields"
        primary-key="rowKey"
        :tbody-tr-class="rowClass"
        striped
        hover
        responsive
      >
        <template v-slot:cell(active)="{ item }">
          <b-button
            v-if="isAddedByHand(item)"
            size="sm"
            variant="link"
            class="p-0 text-danger"
            :title="$t('efficiencyTestDetails.comparisonCriteria.coverage.remove')"
            @click="onRemove(item)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              <line x1="10" x2="10" y1="11" y2="17" />
              <line x1="14" x2="14" y1="11" y2="17" />
            </svg>
          </b-button>
          <b-form-checkbox
            v-else
            switch
            :checked="item.status !== 'Excluded'"
            @change="onToggle(item, $event)"
          ></b-form-checkbox>
        </template>
        <template v-slot:cell(isin)="{ item }">
          <!-- One line: the icon must not wrap under the ISIN in a narrow column. -->
          <div class="d-inline-flex align-items-center text-nowrap">
            <span>{{ item.isin }}</span>
            <failure-icon
              v-if="rowFailures(item).length"
              :type="rowFailureType(item)"
              :text="rowFailureText(item)"
            ></failure-icon>
          </div>
        </template>
        <template v-slot:cell(issuerCode)="{ item }">
          <span v-if="item.isPendingImport">{{ item.issuerName || item.issuerCode }}</span>
          <span v-else class="pointer" style="color: var(--secondaryColor)" @click="onClickTableRow(item)">
            {{ item.issuerName || item.issuerCode }}
          </span>
        </template>
        <template v-slot:cell(name)="{ item }">
          <span v-if="item.isPendingImport">{{ item.name }}</span>
          <span v-else class="pointer" style="color: var(--secondaryColor)" @click="onClickTableRow(item)">
            {{ item.name }}
          </span>
        </template>
        <template v-slot:cell(issueDate)="{ item }">
          {{ item.issueDate ? $d(new Date(item.issueDate), 'short') : $t('common.notAvailable') }}
        </template>
        <template v-slot:cell(maturityDate)="{ item }">
          {{ item.maturityDate ? $d(new Date(item.maturityDate), 'short') : $t('common.notAvailable') }}
        </template>
      </b-table>
    </template>
    <no-content
      v-else
      :icon-name="'exclamation-circle'"
      :icon-class="['mb-3', 'fa-3x']"
      :text="$t('efficiencyTestDetails.comparisonCriteria.coverage.noComparables')"
    ></no-content>
  </div>
</template>

<script>
import NoContent from '../../components/Lib/NoContent'
import FailureIcon from '../../components/EfficiencyTests/FailureIcon.vue'
import { compareFailuresWithPeers } from '../../utils/failureComparison'
import { FAILURE_TYPES } from '../../utils/failurePresentation'
import { formatPercentage } from '../../utils/formatter'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapActions } = createNamespacedHelpers('efficiencyTestsCoverageDetail')

// Columns of the "payout"/"indicators" layouts, read from the coverage item's property of the same
// name. `options` is the search.options.* group translating enum values; booleans render as yes/no;
// `label` is the key of the header when it is not the property's own name.
const LAYOUT_COLUMNS = {
  payout: {
    protectionType: { options: 'ProtectionType' },
    capitalProtection: { percent: true },
    barrierLevel: { percent: true },
    couponType: { options: 'CouponType' },
    couponFrequency: { options: 'CouponFrequency' },
    couponConditioning: { options: 'CouponConditioning' },
    couponBarrierLevel: { percent: true },
    couponMemory: {},
    isAutocallable: {},
    autocallThresholdMin: { percent: true },
    autocallThresholdMax: { percent: true },
    isCallable: {},
  },
  // One column per indicator, showing the max of the API's min/max pair (equal on a single-option product).
  indicators: {
    sriMax: { label: 'sri' },
    rhpMax: { label: 'rhp' },
    riyMax: { label: 'riy', percent: true },
    costsMax: { label: 'costs', percent: true },
    complexityAlert: {},
  },
}

export default {
  components: {
    NoContent,
    FailureIcon,
  },

  data() {
    return {
      isinInput: '',
      layouts: ['general', 'payout', 'indicators'],
      selectedLayout: 'general',
    }
  },

  computed: {
    ...mapGetters([
      'getCoverageCheckItems',
      'getCoverageCheckStats',
      'getCoverageCheckWarnings',
      'getLastExecution',
    ]),

    // Each row is keyed by the certificate it shows, not by its position: the answer to an exclusion lists the
    // certificates in another order, and a switch kept by position would carry the state of the one that was there.
    coverageItems() {
      return this.getCoverageCheckItems.map((item) => ({
        ...item,
        rowKey: [item.isin, item.name, item.referenceDate, item.origin].join('|'),
      }))
    },

    coverageStats() {
      return this.getCoverageCheckStats
    },

    coverageWarnings() {
      return this.getCoverageCheckWarnings
    },

    // The reasons of the last execution of the test being duplicated, against the peers of the draft: worked
    // out each time, never written back, so they return when the criteria are put back as they were.
    failureComparison() {
      return compareFailuresWithPeers(this.getLastExecution, this.coverageItems)
    },

    failureBanner() {
      return this.failureComparison.banner
    },

    failureBannerSummary() {
      const { blocking, retry } = this.failureBanner
      return blocking + retry > 0
        ? this.$t('efficiencyTestFailures.banner.summary', { blocking, retry })
        : this.$t('efficiencyTestFailures.banner.lastNotCompleted')
    },

    itemsFields() {
      const baseFields = [
        { key: 'active', label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.active') },
        { key: 'issuerCode', label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.issuer') },
        { key: 'name', label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.name') },
      ]

      const layoutColumns = LAYOUT_COLUMNS[this.selectedLayout]
      if (layoutColumns) {
        return [
          ...baseFields,
          ...Object.entries(layoutColumns).map(([key, column]) => ({
            key: `${this.selectedLayout}.${key}`,
            label: this.$t(`efficiencyTestDetails.comparisonCriteria.coverage.columns.${column.label || key}`),
            formatter: (value) => this.formatLayoutValue(value, column),
          })),
        ]
      }

      return [
        ...baseFields,
        { key: 'isin', label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.isin') },
        { key: 'issueDate', label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.issueDate') },
        {
          key: 'maturityDate',
          label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.maturityDate'),
        },
        {
          key: 'acepiCategory',
          label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.acepiCategory'),
          formatter: (value) => this.translateOrRaw(`search.options.AcepiCategory.${value}`, value),
        },
        {
          key: 'market',
          label: this.$t('efficiencyTestDetails.comparisonCriteria.coverage.columns.market'),
          formatter: (value) =>
            this.translateOrRaw(`efficiencyTestDetails.comparisonCriteria.coverage.markets.${value}`, value),
        },
      ]
    },
  },

  methods: {
    ...mapActions([
      'checkCoverage',
      'excludeCoverageCheckProduct',
      'reincludeCoverageCheckProduct',
      'removeCoverageCheckIncludedProduct',
      'includeCoverageCheckProductByIsin',
    ]),

    // Criteria live in ComparisonCriteria.vue's own local state (sibling tab, not Vuex), so the page
    // (EfficiencyTestsCreate.vue/EfficiencyTestsEdit.vue) reads them from its own ref and hands them
    // over from its activate-tab handler. They can't be read from here via $parent: this component's
    // parent is the <b-tab> wrapping it, not the page.
    refreshCoverage(criteria) {
      return this.checkCoverage(criteria).catch((err) => {
        this.httpErrorsHandler(err)
      })
    },

    rowFailures(item) {
      return this.failureComparison.rows[(item.isin || '').trim().toUpperCase()] || []
    },

    // What needs a change shows the red icon even when the row also has something to retry.
    rowFailureType(item) {
      return this.rowFailures(item).some((f) => f.type === FAILURE_TYPES.BLOCKING)
        ? FAILURE_TYPES.BLOCKING
        : FAILURE_TYPES.TRANSIENT
    },

    rowFailureText(item) {
      return this.rowFailures(item)
        .map((f) => this.$t(f.textKey, f.textParams))
        .join(' ')
    },

    // Backend sends enum names; a value without a translation falls back to the raw name.
    translateOrRaw(key, value) {
      return value && this.$te(key) ? this.$t(key) : value
    },

    // Codes without a translation yet fall back to the raw code.
    warningText(warning) {
      const key = `efficiencyTestDetails.comparisonCriteria.coverage.warnings.${warning.code}`
      return this.$te(key) ? this.$t(key, { count: warning.count, limit: warning.limit }) : warning.code
    },

    formatLayoutValue(value, column) {
      if (value == null) {
        return this.$t('common.notAvailable')
      }
      if (typeof value === 'boolean') {
        return this.$t(value ? 'common.yes' : 'common.no')
      }
      if (column.percent) {
        return formatPercentage(value)
      }
      if (column.options) {
        const key = `search.options.${column.options}.${value}`
        return this.$te(key) ? this.$t(key) : value
      }
      return value
    },

    // A secondary-market certificate not imported yet has no product page to open.
    onClickTableRow(item) {
      if (item.isPendingImport) {
        return
      }
      this.openProductDetails(item, this.$store.getters['efficiencyTestsCommon/getScoringProfileId'])
    },

    // Only the certificates the engine proposes can be excluded; the ones added one by one are
    // deleted instead, they were never part of the proposal.
    isAddedByHand(item) {
      // The status, not the origin: a certificate added by ISIN reaches the backend without an origin
      // when it is one of OKV's, so it comes back as "Criteria" — but "Included" is only ever a manual pick.
      return item.status === 'Included'
    },

    rowClass(item) {
      return item && item.status === 'Excluded' ? 'text-muted excluded-row' : ''
    },

    onToggle(item, checked) {
      const action = checked ? this.reincludeCoverageCheckProduct : this.excludeCoverageCheckProduct
      action(item).catch((err) => {
        this.httpErrorsHandler(err)
      })
    },

    onRemove(item) {
      this.removeCoverageCheckIncludedProduct(item).catch((err) => {
        this.httpErrorsHandler(err)
      })
    },

    // Whether the certificate is already part of the comparison, by the criteria or added by hand: only an
    // excluded one is not, and including it again brings it back.
    isIncluded(isin) {
      const normalized = (isin || '').trim().toUpperCase()
      return this.coverageItems.some(
        (item) => (item.isin || '').trim().toUpperCase() === normalized && item.status !== 'Excluded'
      )
    },

    onIncludeByIsin() {
      if (!this.isinInput) {
        return
      }
      const isin = this.isinInput
      if (this.isIncluded(isin)) {
        this.isinInput = ''
        this.$bvToast.toast(this.$t('efficiencyTestDetails.comparisonCriteria.coverage.isinAlreadyIncluded', { isin }), {
          title: this.$t('efficiencyTestDetails.profileCoverageDetail'),
          variant: 'info',
        })
        return
      }
      this.includeCoverageCheckProductByIsin(isin)
        .then(() => {
          this.isinInput = ''
        })
        .catch((err) => {
          // An unknown ISIN is a user input issue, not a page-level error — keep the user here.
          if (err && err.response && err.response.status === 404) {
            this.$bvToast.toast(this.$t('efficiencyTestDetails.comparisonCriteria.coverage.isinNotFound', { isin }), {
              title: this.$t('efficiencyTestDetails.profileCoverageDetail'),
              variant: 'warning',
            })
            return
          }
          this.httpErrorsHandler(err)
        })
    },
  },
}
</script>

<style scoped>
::v-deep .excluded-row {
  opacity: 0.55;
}

.pointer {
  cursor: pointer;
}

.layout-dropdown ::v-deep .dropdown-item {
  padding: 10px 20px;
  color: black !important;
  font-size: 0.8rem;
}
.layout-dropdown ::v-deep .dropdown-item a:active {
  background-color: #f8f9fa;
}
.layout-dropdown ::v-deep .dropdown-item:hover,
.layout-dropdown ::v-deep .dropdown-item:focus {
  color: #16181b;
  text-decoration: none;
  background-color: #e9ecef;
}
</style>
